// src/context/DataContext.jsx
//
// Thin composition layer over the four focused context providers:
//   LeagueDataContext  — raw data, loading state, load actions
//   ManagerContext     — selected manager, selection helpers, per-manager metrics
//   LeagueAwardsContext — filtered league awards
//   PrizeContext       — computed prize data and winners
//
// Use the focused hooks for all new code:
//   useLeagueData()       for raw data / loading state
//   useManagerContext()   for manager selection / getManagerData
//   useLeagueAwards()     for award data
//   usePrize()            for prize configuration
//
import React from 'react';
import { LeagueDataProvider } from './LeagueDataContext';
import { ManagerProvider } from './ManagerContext';
import { LeagueAwardsProvider } from './LeagueAwardsContext';
import { PrizeProvider } from './PrizeContext';

// Re-export focused hooks so components can import from a single location
export { useLeagueData } from './LeagueDataContext';
export { useManagerContext } from './ManagerContext';
export { useLeagueAwards } from './LeagueAwardsContext';
export { usePrize } from './PrizeContext';

// ---------------------------------------------------------------------------
// DataProvider — composes all focused providers
// ---------------------------------------------------------------------------

/**
 * Top-level data provider.  Wraps the application with all four focused
 * context providers.
 *
 * Usage:
 *   <DataProvider>
 *     <App />
 *   </DataProvider>
 */
export const DataProvider = ({ children }) => (
  <LeagueDataProvider>
    <ManagerProvider>
      <LeagueAwardsProvider>
        <PrizeProvider>
          {children}
        </PrizeProvider>
      </LeagueAwardsProvider>
    </ManagerProvider>
  </LeagueDataProvider>
);
